import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Form1099INT } from "./interface/1099-INT-interface";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import { v4 as uuidv4 } from "uuid";
import { parseAmount } from "../../constants/validationFunctions";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { validateIRSXml } from "../../schema/xsdValidation";

/**
 * Helper function to convert country names to IRS country codes
 */
function getCountryCode(countryName: string | undefined): string {
  if (!countryName) return "";

  const normalizedName = countryName.trim().toUpperCase();

  const countryMap: Record<string, string> = {
    CANADA: "CA",
    "UNITED STATES": "US",
    MEXICO: "MX",
    "UNITED KINGDOM": "UK",
    GERMANY: "GM",
    FRANCE: "FR",
    AUSTRALIA: "AS",
    CHINA: "CH",
    JAPAN: "JA",
    BRAZIL: "BR",
    INDIA: "IN",
    SPAIN: "SP",
    ITALY: "IT",
    NETHERLANDS: "NL",
    RUSSIA: "RS",
  };

  if (countryMap[normalizedName]) {
    return countryMap[normalizedName];
  }

  if (normalizedName.length === 2 && /^[A-Z]{2}$/.test(normalizedName)) {
    return normalizedName;
  }

  return "CA";
}

// Define TypeScript interfaces for the structure
interface IssuerDetailType {
  ForeignEntityInd: string;
  TIN: string;
  TINSubmittedTypeCd: string;
  PersonNameControlTxt: string;
  PersonName?: {
    PersonFirstNm: string;
    PersonLastNm: string;
  };
  MailingAddressGrp?: {
    USAddress: {
      AddressLine1Txt?: string;
      AddressLine2Txt?: string;
      CityNm?: string;
      StateAbbreviationCd?: string;
      ZIPCd?: string;
    };
  };
  PhoneNum?: string;
}

interface TotalsType {
  InterestIncomeAmt?: string;
  EarlyWithdrawalPenaltyAmt?: string;
  USSavingsBondsTreasObligIntAmt?: string;
  FederalIncomeTaxWithheldAmt?: string;
  InvestmentExpenseAmt?: string;
  ForeignTaxesPaidAmt?: string;
  TaxExemptInterestAmt?: string;
  SpcfdPrvtActyBondInterestAmt?: string;
  MarketDiscountAmt?: string;
  BondPremiumAmt?: string;
  TreasuryObligBondPremiumAmt?: string;
  TaxExemptBondPremiumAmt?: string;
}

/**
 * Format amount as decimal with 2 decimal places (USDecimalAmountNNType)
 */
function formatIrsAmount(amount: string | number): string {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) {
    return "0.00";
  }

  return numericAmount.toFixed(2);
}

function formatCusip(cusip: string): string {
  if (!cusip) return "";
  return cusip.replace(/[^A-Za-z0-9]/g, "");
}

/**
 * Format routing transit number (9 digits)
 */
function formatRoutingTransitNum(rtn: string | undefined): string | undefined {
  if (!rtn) return undefined;
  const cleaned = rtn.replace(/[^\d]/g, "");
  if (cleaned.length === 9) {
    return cleaned;
  }
  return undefined;
}

export async function generate1099INTXml(
  ctx: EngineContext,
  records: Form1099INT[],
  options?: { validate?: boolean }
) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  const transmitterData = ctx.transmitter as any;

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);

  // Step 1: Group records by FILER_FEDERAL_ID_NUMBER
  const recordsByFiler: Record<string, Form1099INT[]> = {};

  records.forEach((record) => {
    if (!record.FILER_FEDERAL_ID_NUMBER) {
      console.warn("Record missing FILER_FEDERAL_ID_NUMBER, using default");
      record.FILER_FEDERAL_ID_NUMBER = "000000000";
    }

    const filerId = record.FILER_FEDERAL_ID_NUMBER;
    if (!recordsByFiler[filerId]) {
      recordsByFiler[filerId] = [];
    }
    recordsByFiler[filerId].push(record);
  });

  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);

  // Step 2: Create a submission group for each filer
  const irSubmissionGroups = Object.keys(recordsByFiler).map((filerId) => {
    const filerRecords = recordsByFiler[filerId];
    const firstRecord = filerRecords[0];

    const submissionId = generateUniqueSubmissionId();

    // Step 3: Build the IssuerDetail
    const issuerDetail: IssuerDetailType = {
      ForeignEntityInd: "0",
      TIN: ctx.irs.testIndicator
        ? "000000000"
        : (firstRecord.FILER_FEDERAL_ID_NUMBER || "000000000").replace(
            /-/g,
            ""
          ),
      TINSubmittedTypeCd: "INDIVIDUAL_TIN",
      PersonNameControlTxt: firstRecord.FILER_NAME
        ? firstRecord.FILER_NAME.trim().substring(0, 4).toUpperCase()
        : "PAYS",
    };

    // PersonName is REQUIRED before MailingAddressGrp — always emit it (with a fallback) so an
    // issuer/filer missing FILER_NAME doesn't produce invalid structure (MailingAddressGrp where
    // the name is expected). The shared name sanitizer cleans/length-caps the value afterward.
    issuerDetail.PersonName = {
      PersonFirstNm: firstRecord.FILER_NAME || "UNKNOWN",
      PersonLastNm: firstRecord.FILER_NAME || "FILER",
    };

    if (
      firstRecord.FILER_ADDRESS1 ||
      firstRecord.FILER_CITY ||
      firstRecord.FILER_STATE ||
      firstRecord.FILER_ZIP
    ) {
      issuerDetail.MailingAddressGrp = {
        USAddress: {},
      };

      if (firstRecord.FILER_ADDRESS1) {
        issuerDetail.MailingAddressGrp.USAddress.AddressLine1Txt =
          firstRecord.FILER_ADDRESS1;
      }

      if (firstRecord.FILER_ADDRESS2) {
        issuerDetail.MailingAddressGrp.USAddress.AddressLine2Txt =
          firstRecord.FILER_ADDRESS2;
      }

      if (firstRecord.FILER_CITY) {
        issuerDetail.MailingAddressGrp.USAddress.CityNm =
          firstRecord.FILER_CITY;
      }

      if (firstRecord.FILER_STATE) {
        issuerDetail.MailingAddressGrp.USAddress.StateAbbreviationCd =
          firstRecord.FILER_STATE;
      }

      if (firstRecord.FILER_ZIP) {
        issuerDetail.MailingAddressGrp.USAddress.ZIPCd = firstRecord.FILER_ZIP;
      }
    }

    if (firstRecord.FILER_PHONE) {
      issuerDetail.PhoneNum = firstRecord.FILER_PHONE.replace(/[^\d]/g, "");
    }

    // Step 4: Process recipient records for this filer
    const form1099INTDetails = filerRecords.map((record) => {
      if (!record.RECIPIENT_ID_NUMBER) {
        console.warn("Record missing RECIPIENT_ID_NUMBER");
      }

      if (!record.RECIPIENT_NAME) {
        console.warn("Record missing RECIPIENT_NAME");
      }

      const validState =
        record.RECIPIENT_STATE &&
        [
          "AL",
          "AK",
          "AS",
          "AZ",
          "AR",
          "CA",
          "CO",
          "MP",
          "CT",
          "DE",
          "DC",
          "FM",
          "FL",
          "GA",
          "GU",
          "HI",
          "ID",
          "IL",
          "IN",
          "IA",
          "KS",
          "KY",
          "LA",
          "ME",
          "MH",
          "MD",
          "MA",
          "MI",
          "MN",
          "MS",
          "MO",
          "MT",
          "NE",
          "NV",
          "NH",
          "NJ",
          "NM",
          "NY",
          "NC",
          "ND",
          "OH",
          "OK",
          "OR",
          "PW",
          "PA",
          "PR",
          "RI",
          "SC",
          "SD",
          "TN",
          "TX",
          "VI",
          "UT",
          "VT",
          "VA",
          "WA",
          "WV",
          "WI",
          "WY",
          "AA",
          "AE",
          "AP",
        ].includes(record.RECIPIENT_STATE?.toUpperCase())
          ? record.RECIPIENT_STATE.toUpperCase()
          : "IL";

      const isForeignAddress =
        record.FOREIGN_COUNTRY_INDICATOR && record.FOREIGN_COUNTRY;

      // Build form detail in EXACT schema sequence order
      const formDetail: any = {
        TaxYr: taxYear.toString(),
        RecordId: record.id.toString(),
      };

      // IssuerOfficeCd (optional)
      if (record.ISSUER_OFFICE_CD) {
        formDetail.IssuerOfficeCd = record.ISSUER_OFFICE_CD.substring(0, 4);
      }

      // CFSFElectionStateCd is never written: this form sends CFSFElectionInd 0, and a state
      // election under a submission that does not claim Combined Federal/State Filing is a
      // contradiction. An uploaded CFSF_ELECTION_STATE_CD column used to be copied in as is.

      // VoidInd (required, fixed "0")
      formDetail.VoidInd = "0";

      // CorrectedInd (required)
      formDetail.CorrectedInd = record.corrected ? "1" : "0";

      // PrevSubmittedRecRecipientGrp (optional, for corrections)
      if (record.corrected && record.PREV_UNIQUE_RECORD_ID) {
        formDetail.PrevSubmittedRecRecipientGrp = {
          UniqueRecordId: record.PREV_UNIQUE_RECORD_ID,
          ...(record.PREV_RECIPIENT_NAME && {
            RecipientNm: record.PREV_RECIPIENT_NAME,
          }),
          ...(record.PREV_RECIPIENT_TIN && {
            RecipientTIN: record.PREV_RECIPIENT_TIN.replace(/-/g, ""),
          }),
        };
      }

      // RecipientDetail (required). TY2026 layout-V2 records (RECIPIENT_ID_TYPE /
      // split names / business names) file via the shared split builder (business
      // as BusinessName+BUSINESS_TIN); legacy records keep the shape below.
      const intTin = ctx.irs.testIndicator
        ? "000000000"
        : (record.RECIPIENT_ID_NUMBER || "000000000").replace(/-/g, "");
      const intMailing = isForeignAddress
        ? {
            ForeignAddress: {
              AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
              ...(record.RECIPIENT_ADDRESS2 && {
                AddressLine2Txt: record.RECIPIENT_ADDRESS2,
              }),
              CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
              CountryCd: getCountryCode(record.FOREIGN_COUNTRY),
              ...(record.RECIPIENT_ZIP && {
                ForeignPostalCd: record.RECIPIENT_ZIP,
              }),
            },
          }
        : {
            USAddress: {
              AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
              ...(record.RECIPIENT_ADDRESS2 && {
                AddressLine2Txt: record.RECIPIENT_ADDRESS2,
              }),
              CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
              StateAbbreviationCd: validState,
              ZIPCd: record.RECIPIENT_ZIP || "00000",
            },
          };
      formDetail.RecipientDetail = buildSplitRecipientDetail(
        record,
        intTin,
        intMailing
      ) ?? {
        TIN: intTin,
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        PersonNameControlTxt: (record.RECIPIENT_NAME || "UNKN")
          .trim()
          .substring(0, 4)
          .toUpperCase(),
        PersonName: {
          PersonFirstNm: record.RECIPIENT_NAME || "Unknown",
          PersonLastNm: record.RECIPIENT_NAME2 || "RECIPIENT",
        },
        MailingAddressGrp: isForeignAddress
          ? {
              ForeignAddress: {
                AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
                ...(record.RECIPIENT_ADDRESS2 && {
                  AddressLine2Txt: record.RECIPIENT_ADDRESS2,
                }),
                CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
                CountryCd: getCountryCode(record.FOREIGN_COUNTRY),
                ...(record.RECIPIENT_ZIP && {
                  ForeignPostalCd: record.RECIPIENT_ZIP,
                }),
              },
            }
          : {
              USAddress: {
                AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
                ...(record.RECIPIENT_ADDRESS2 && {
                  AddressLine2Txt: record.RECIPIENT_ADDRESS2,
                }),
                CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
                StateAbbreviationCd: validState,
                ZIPCd: record.RECIPIENT_ZIP || "00000",
              },
            },
      };

      // RecipientAccountNum (optional)
      if (record.ACCOUNT_NUMBER) {
        formDetail.RecipientAccountNum = record.ACCOUNT_NUMBER;
      }

      // SecondTINNoticeInd (required)
      formDetail.SecondTINNoticeInd =
        record.BOX_2ND_TIN_NOT_IND === "X" ? "1" : "0";

      // FATCAFilingRequirementInd (required)
      formDetail.FATCAFilingRequirementInd =
        record.FATCA_REQUIREMENT === "X" ? "1" : "0";

      // PayerRoutingTransitNum (optional) - NEW for TY2025
      const routingNum = formatRoutingTransitNum(
        record.PAYER_ROUTING_TRANSIT_NUM
      );
      if (routingNum) {
        formDetail.PayerRoutingTransitNum = routingNum;
      }

      // Box 1: InterestIncomeAmt (optional)
      if (record.BOX1_INTEREST_INCOME_NOT_IN_BOX3) {
        const amt = parseFloat(
          parseAmount(record.BOX1_INTEREST_INCOME_NOT_IN_BOX3)
        );
        if (amt > 0) {
          formDetail.InterestIncomeAmt = formatIrsAmount(amt);
        }
      }

      // Box 2: EarlyWithdrawalPenaltyAmt (optional)
      if (record.BOX2_EARLY_WITHDRAWAL_PENALTY) {
        const amt = parseFloat(
          parseAmount(record.BOX2_EARLY_WITHDRAWAL_PENALTY)
        );
        if (amt > 0) {
          formDetail.EarlyWithdrawalPenaltyAmt = formatIrsAmount(amt);
        }
      }

      // Box 3: USSavingsBondsTreasObligIntAmt (optional)
      if (record.BOX3_INTEREST_ON_US_SAVINGS) {
        const amt = parseFloat(
          parseAmount(String(record.BOX3_INTEREST_ON_US_SAVINGS))
        );
        if (amt > 0) {
          formDetail.USSavingsBondsTreasObligIntAmt = formatIrsAmount(amt);
        }
      }

      // Box 4: FederalIncomeTaxWithheldAmt (optional)
      if (record.BOX4_FED_INCOME_TAX_WITHHELD) {
        const amt = parseFloat(
          parseAmount(String(record.BOX4_FED_INCOME_TAX_WITHHELD))
        );
        if (amt > 0) {
          formDetail.FederalIncomeTaxWithheldAmt = formatIrsAmount(amt);
        }
      }

      // Box 5: InvestmentExpenseAmt (optional)
      if (record.BOX5_INVESTMENT_EXPENSES) {
        const amt = parseFloat(
          parseAmount(String(record.BOX5_INVESTMENT_EXPENSES))
        );
        if (amt > 0) {
          formDetail.InvestmentExpenseAmt = formatIrsAmount(amt);
        }
      }

      // Box 6: ForeignTaxesPaidAmt (optional)
      if (record.BOX6_FOREIGN_TAX_PAID) {
        const amt = parseFloat(
          parseAmount(String(record.BOX6_FOREIGN_TAX_PAID))
        );
        if (amt > 0) {
          formDetail.ForeignTaxesPaidAmt = formatIrsAmount(amt);
        }
      }

      // Box 7: ForeignCountryOrUSPossessionCd (optional)
      if (record.BOX7_FOREIGN_COUNTRY_US_POSSESSION) {
        formDetail.ForeignCountryOrUSPossessionCd = getCountryCode(
          record.BOX7_FOREIGN_COUNTRY_US_POSSESSION
        );
      }

      // Box 8: TaxExemptInterestAmt (optional)
      if (record.BOX8_TAX_EXEMPT_INTEREST) {
        const amt = parseFloat(
          parseAmount(String(record.BOX8_TAX_EXEMPT_INTEREST))
        );
        if (amt > 0) {
          formDetail.TaxExemptInterestAmt = formatIrsAmount(amt);
        }
      }

      // Box 9: SpcfdPrvtActyBondInterestAmt (optional)
      if (record.BOX9_SPEC_PRIVATE_ACT_BOND_INT) {
        const amt = parseFloat(
          parseAmount(String(record.BOX9_SPEC_PRIVATE_ACT_BOND_INT))
        );
        if (amt > 0) {
          formDetail.SpcfdPrvtActyBondInterestAmt = formatIrsAmount(amt);
        }
      }

      // Box 10: MarketDiscountAmt (optional)
      if (record.BOX10_MARKET_DISCOUNT_BOND) {
        const amt = parseFloat(
          parseAmount(String(record.BOX10_MARKET_DISCOUNT_BOND))
        );
        if (amt > 0) {
          formDetail.MarketDiscountAmt = formatIrsAmount(amt);
        }
      }

      // Box 11: BondPremiumAmt (optional)
      if (record.BOX11_BOND_PREMIUM) {
        const amt = parseFloat(parseAmount(String(record.BOX11_BOND_PREMIUM)));
        if (amt > 0) {
          formDetail.BondPremiumAmt = formatIrsAmount(amt);
        }
      }

      // Box 12: TreasuryObligBondPremiumAmt (optional)
      if (record.BOX12_BOND_PREMIUM_ON_TREASURY) {
        const amt = parseFloat(
          parseAmount(String(record.BOX12_BOND_PREMIUM_ON_TREASURY))
        );
        if (amt > 0) {
          formDetail.TreasuryObligBondPremiumAmt = formatIrsAmount(amt);
        }
      }

      // Box 13: TaxExemptBondPremiumAmt (optional)
      if (record.BOX13_TAX_EXEMPT_BOND) {
        const amt = parseFloat(
          parseAmount(String(record.BOX13_TAX_EXEMPT_BOND))
        );
        if (amt > 0) {
          formDetail.TaxExemptBondPremiumAmt = formatIrsAmount(amt);
        }
      }

      // Box 14: TaxExemptTaxCreditBondCUSIPNum (optional)
      if (record.BOX14_TAX_EXEMPT_BOND_CUSIP) {
        const cusip = formatCusip(record.BOX14_TAX_EXEMPT_BOND_CUSIP);
        if (cusip) {
          formDetail.TaxExemptTaxCreditBondCUSIPNum = cusip;
        }
      }

      // StateLocalTaxGrp (optional, can repeat)
      if (shouldIncludeStateLocalTaxGrp(record)) {
        formDetail.StateLocalTaxGrp = getStateLocalTaxGrp(record);
      }

      return formDetail;
    });

    // Calculate totals for this filer's records
    const totals = calculateTotalsForFiler(filerRecords);

    // Filter out zero-value totals
    const filteredTotals: TotalsType = {};
    if (parseFloat(totals.InterestIncomeAmt || "0") > 0) {
      filteredTotals.InterestIncomeAmt = totals.InterestIncomeAmt;
    }
    if (parseFloat(totals.EarlyWithdrawalPenaltyAmt || "0") > 0) {
      filteredTotals.EarlyWithdrawalPenaltyAmt =
        totals.EarlyWithdrawalPenaltyAmt;
    }
    if (parseFloat(totals.USSavingsBondsTreasObligIntAmt || "0") > 0) {
      filteredTotals.USSavingsBondsTreasObligIntAmt =
        totals.USSavingsBondsTreasObligIntAmt;
    }
    if (parseFloat(totals.FederalIncomeTaxWithheldAmt || "0") > 0) {
      filteredTotals.FederalIncomeTaxWithheldAmt =
        totals.FederalIncomeTaxWithheldAmt;
    }
    if (parseFloat(totals.InvestmentExpenseAmt || "0") > 0) {
      filteredTotals.InvestmentExpenseAmt = totals.InvestmentExpenseAmt;
    }
    if (parseFloat(totals.ForeignTaxesPaidAmt || "0") > 0) {
      filteredTotals.ForeignTaxesPaidAmt = totals.ForeignTaxesPaidAmt;
    }
    if (parseFloat(totals.TaxExemptInterestAmt || "0") > 0) {
      filteredTotals.TaxExemptInterestAmt = totals.TaxExemptInterestAmt;
    }
    if (parseFloat(totals.SpcfdPrvtActyBondInterestAmt || "0") > 0) {
      filteredTotals.SpcfdPrvtActyBondInterestAmt =
        totals.SpcfdPrvtActyBondInterestAmt;
    }
    if (parseFloat(totals.MarketDiscountAmt || "0") > 0) {
      filteredTotals.MarketDiscountAmt = totals.MarketDiscountAmt;
    }
    if (parseFloat(totals.BondPremiumAmt || "0") > 0) {
      filteredTotals.BondPremiumAmt = totals.BondPremiumAmt;
    }
    if (parseFloat(totals.TreasuryObligBondPremiumAmt || "0") > 0) {
      filteredTotals.TreasuryObligBondPremiumAmt =
        totals.TreasuryObligBondPremiumAmt;
    }
    if (parseFloat(totals.TaxExemptBondPremiumAmt || "0") > 0) {
      filteredTotals.TaxExemptBondPremiumAmt = totals.TaxExemptBondPremiumAmt;
    }

    // Step 5: Return a submission group for this filer
    return {
      IRSubmission1Header: {
        SubmissionId: submissionId,
        TaxYr: taxYear,
        IssuerDetail: issuerDetail,
        ContactPersonInformationGrp: {
          ContactPersonNm: firstRecord.FILER_NAME || "Contact Person",
          ContactPhoneNum:
            firstRecord.FILER_PHONE?.replace(/[^\d]/g, "") || "0000000000",
          ContactEmailAddressTxt: "support@ts1099.com",
        },
        FormTypeCd: "1099INT",
        ParentFormTypeCd: "1096",
        CFSFElectionInd: "0",
        JuratSignatureGrp: {
          SignatureIntentInd: "1",
          JuratSignaturePIN: "00000",
          SignatureDt: new Date().toISOString().split("T")[0],
          JuratPersonTitleTxt: "Payer",
          PersonNm: firstRecord.FILER_NAME || "Payer",
        },
        TotalReportedRcpntFormCnt: form1099INTDetails.length.toString(),
        IRSubmission1FormTotals: {
          Form1099INTTotalAmtGrp: filteredTotals,
        },
      },
      IRSubmission1Detail: {
        Form1099INTDetail: form1099INTDetails,
      },
    };
  });

  // Build the complete envelope structure
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear), // NEW for TY2025 - must come before UniqueTransmissionId
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: "0",
        TransmissionTypeCd: "O",
        TestCd: ctx.irs.testIndicator ? "T" : "P",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: Object.keys(recordsByFiler).length.toString(),
        TotalRecipientFormCnt: records.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: irSubmissionGroups,
    },
  };

  try {
    const xml = builder.build(xmlStructure);

    // Do NOT validate here: the shared generateXmlForForm pipeline sanitizes names/addresses/
    // amounts FIRST, then runs the form-agnostic fail-fast validation with grouped, readable
    // errors. Validating the RAW XML here bypasses those sanitizers and aborts with a raw dump.
    const shouldValidate = false;
    if (shouldValidate) {
      try {
        const validationResponse = validateIRSXml(xml);
        if (validationResponse.length > 0) {
          console.error("XML Validation Errors:", validationResponse);
          throw new Error(
            `XML failed validation against IRS schema: ${JSON.stringify(
              validationResponse
            )}`
          );
        }
        console.log("✅ XML validation passed against IRS schema");
      } catch (validationError: any) {
        console.error("XML Validation Error:", validationError);
        throw validationError;
      }
    }

    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}

// Helper function to calculate totals for a specific filer's records
// According to the 2025 XSD schema for Form1099INTTotalAmtGrpType
function calculateTotalsForFiler(records: Form1099INT[]): TotalsType {
  const totals: TotalsType = {
    InterestIncomeAmt: "0.00",
    EarlyWithdrawalPenaltyAmt: "0.00",
    USSavingsBondsTreasObligIntAmt: "0.00",
    FederalIncomeTaxWithheldAmt: "0.00",
    InvestmentExpenseAmt: "0.00",
    ForeignTaxesPaidAmt: "0.00",
    TaxExemptInterestAmt: "0.00",
    SpcfdPrvtActyBondInterestAmt: "0.00",
    MarketDiscountAmt: "0.00",
    BondPremiumAmt: "0.00",
    TreasuryObligBondPremiumAmt: "0.00",
    TaxExemptBondPremiumAmt: "0.00",
  };

  records.forEach((record) => {
    if (record.BOX1_INTEREST_INCOME_NOT_IN_BOX3) {
      const amount = parseFloat(
        parseAmount(record.BOX1_INTEREST_INCOME_NOT_IN_BOX3)
      );
      totals.InterestIncomeAmt = formatIrsAmount(
        parseFloat(totals.InterestIncomeAmt!) + amount
      );
    }

    if (record.BOX2_EARLY_WITHDRAWAL_PENALTY) {
      const amount = parseFloat(
        parseAmount(record.BOX2_EARLY_WITHDRAWAL_PENALTY)
      );
      totals.EarlyWithdrawalPenaltyAmt = formatIrsAmount(
        parseFloat(totals.EarlyWithdrawalPenaltyAmt!) + amount
      );
    }

    if (record.BOX3_INTEREST_ON_US_SAVINGS) {
      const amount = parseFloat(
        parseAmount(String(record.BOX3_INTEREST_ON_US_SAVINGS))
      );
      totals.USSavingsBondsTreasObligIntAmt = formatIrsAmount(
        parseFloat(totals.USSavingsBondsTreasObligIntAmt!) + amount
      );
    }

    if (record.BOX4_FED_INCOME_TAX_WITHHELD) {
      const amount = parseFloat(
        parseAmount(String(record.BOX4_FED_INCOME_TAX_WITHHELD))
      );
      totals.FederalIncomeTaxWithheldAmt = formatIrsAmount(
        parseFloat(totals.FederalIncomeTaxWithheldAmt!) + amount
      );
    }

    if (record.BOX5_INVESTMENT_EXPENSES) {
      const amount = parseFloat(
        parseAmount(String(record.BOX5_INVESTMENT_EXPENSES))
      );
      totals.InvestmentExpenseAmt = formatIrsAmount(
        parseFloat(totals.InvestmentExpenseAmt!) + amount
      );
    }

    if (record.BOX6_FOREIGN_TAX_PAID) {
      const amount = parseFloat(
        parseAmount(String(record.BOX6_FOREIGN_TAX_PAID))
      );
      totals.ForeignTaxesPaidAmt = formatIrsAmount(
        parseFloat(totals.ForeignTaxesPaidAmt!) + amount
      );
    }

    if (record.BOX8_TAX_EXEMPT_INTEREST) {
      const amount = parseFloat(
        parseAmount(String(record.BOX8_TAX_EXEMPT_INTEREST))
      );
      totals.TaxExemptInterestAmt = formatIrsAmount(
        parseFloat(totals.TaxExemptInterestAmt!) + amount
      );
    }

    if (record.BOX9_SPEC_PRIVATE_ACT_BOND_INT) {
      const amount = parseFloat(
        parseAmount(String(record.BOX9_SPEC_PRIVATE_ACT_BOND_INT))
      );
      totals.SpcfdPrvtActyBondInterestAmt = formatIrsAmount(
        parseFloat(totals.SpcfdPrvtActyBondInterestAmt!) + amount
      );
    }

    if (record.BOX10_MARKET_DISCOUNT_BOND) {
      const amount = parseFloat(
        parseAmount(String(record.BOX10_MARKET_DISCOUNT_BOND))
      );
      totals.MarketDiscountAmt = formatIrsAmount(
        parseFloat(totals.MarketDiscountAmt!) + amount
      );
    }

    if (record.BOX11_BOND_PREMIUM) {
      const amount = parseFloat(parseAmount(String(record.BOX11_BOND_PREMIUM)));
      totals.BondPremiumAmt = formatIrsAmount(
        parseFloat(totals.BondPremiumAmt!) + amount
      );
    }

    if (record.BOX12_BOND_PREMIUM_ON_TREASURY) {
      const amount = parseFloat(
        parseAmount(String(record.BOX12_BOND_PREMIUM_ON_TREASURY))
      );
      totals.TreasuryObligBondPremiumAmt = formatIrsAmount(
        parseFloat(totals.TreasuryObligBondPremiumAmt!) + amount
      );
    }

    if (record.BOX13_TAX_EXEMPT_BOND) {
      const amount = parseFloat(
        parseAmount(String(record.BOX13_TAX_EXEMPT_BOND))
      );
      totals.TaxExemptBondPremiumAmt = formatIrsAmount(
        parseFloat(totals.TaxExemptBondPremiumAmt!) + amount
      );
    }
  });

  return totals;
}

/**
 * Helper function to determine if state/local tax groups should be included.
 */
function shouldIncludeStateLocalTaxGrp(record: Form1099INT): boolean {
  return !!(
    (record.BOX15_STATE &&
      (parseFloat(parseAmount(String(record.BOX17_STATE_TAX_WITHHELD || "0"))) >
        0 ||
        parseFloat(parseAmount(String(record.BOX15_STATE || "0"))) > 0)) ||
    (record.BOX15_STATE2 &&
      (parseFloat(
        parseAmount(String(record.BOX17_STATE_TAX_WITHHELD2 || "0"))
      ) > 0 ||
        parseFloat(parseAmount(String(record.BOX15_STATE2 || "0"))) > 0))
  );
}

/**
 * Helper function to build state/local tax group arrays.
 */
function getStateLocalTaxGrp(record: Form1099INT): any[] {
  const groups = [];

  if (
    record.BOX15_STATE &&
    (parseFloat(parseAmount(String(record.BOX17_STATE_TAX_WITHHELD || "0"))) >
      0 ||
      parseFloat(parseAmount(String(record.BOX15_STATE || "0"))) > 0)
  ) {
    groups.push({
      StateAbbreviationCd: record.BOX15_STATE.substring(0, 2),
      StateTaxGrp: {
        StateTaxWithheldAmt: formatIrsAmount(
          parseAmount(String(record.BOX17_STATE_TAX_WITHHELD || "0"))
        ),
      },
    });
  }

  if (
    record.BOX15_STATE2 &&
    (parseFloat(parseAmount(String(record.BOX17_STATE_TAX_WITHHELD2 || "0"))) >
      0 ||
      parseFloat(parseAmount(String(record.BOX15_STATE2 || "0"))) > 0)
  ) {
    groups.push({
      StateAbbreviationCd: record.BOX15_STATE2.substring(0, 2),
      StateTaxGrp: {
        StateTaxWithheldAmt: formatIrsAmount(
          parseAmount(String(record.BOX17_STATE_TAX_WITHHELD2 || "0"))
        ),
      },
    });
  }

  return groups;
}

/**
 * Format the Software ID to ensure it starts with the correct tax year prefix.
 *
 * @param softwareId - The Software ID from environment variable (should be the IRS-assigned ID for the tax year)
 * @param taxYear - The tax year (e.g., "2025")
 * @returns Formatted Software ID with first two digits matching the tax year
 */
function formatSoftwareId(
  softwareId: string | undefined,
  taxYear: string
): string {
  // Get last two digits of tax year (e.g., "25" for 2025)
  const yearSuffix = taxYear.slice(-2);

  // If no software ID provided, return default with year prefix
  // Note: This is a fallback - you should always provide the IRS-assigned Software ID
  if (!softwareId) {
    console.warn(
      `No Software ID provided. Using default. Please use the IRS-assigned Software ID for tax year ${taxYear}.`
    );
    return `${yearSuffix}00000`;
  }

  // If SoftwareId already starts with correct year, return as-is
  // This should be the case for properly assigned Software IDs
  if (softwareId.startsWith(yearSuffix)) {
    return softwareId.substring(0, 10); // Max 10 characters per schema
  }

  // If SoftwareId doesn't start with correct year, warn and fix it
  // This handles cases where an old Software ID from a previous year might be used
  console.warn(
    `Software ID "${softwareId}" does not start with "${yearSuffix}" for tax year ${taxYear}. ` +
      `Updating to match tax year. Please ensure you're using the IRS-assigned Software ID for ${taxYear}.`
  );

  // Replace first two characters with year suffix
  // Keep remaining characters (up to 10 total length)
  const remaining = softwareId.substring(2);
  return `${yearSuffix}${remaining}`.substring(0, 10);
}
